import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="subscription-form"
export default class extends Controller {
  static targets = ["form", "planId", "leads", "planTitle", "planDescription", "planPrice", "modal"]
  
  map = null
  
  connect() {
    // Add event listener for ESC key to close modal
    document.addEventListener('keydown', this.handleKeyDown.bind(this))
    
    // Add event listener for clicking outside the modal
    if (this.hasModalTarget) {
      this.modalTarget.addEventListener('click', this.handleBackgroundClick.bind(this))
    }
    
    // Add event listener for zipcode input
    const zipcodeInput = document.getElementById('zipcode');
    if (zipcodeInput) {
      zipcodeInput.addEventListener('change', (e) => {
        if (e.target.value && typeof L !== 'undefined') {
          this.geocodeZipcode(e.target.value);
        }
        this.validateForm();
      });
    }
    
    // Add event listener for email input
    const emailInput = document.getElementById('email');
    if (emailInput) {
      emailInput.addEventListener('change', () => {
        this.validateForm();
      });
    }
    
    // Add event listener for terms checkbox
    const termsCheckbox = document.getElementById('terms');
    if (termsCheckbox) {
      termsCheckbox.addEventListener('change', () => {
        this.validateForm();
      });
    }
    
    // Initial form validation
    this.validateForm();
  }
  
  disconnect() {
    document.removeEventListener('keydown', this.handleKeyDown.bind(this))
    
    // Clean up map when controller disconnects
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
  
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.closeModal()
    }
  }
  
  handleBackgroundClick(event) {
    // Only close if clicking directly on the modal background, not its children
    if (event.target === this.modalTarget) {
      this.closeModal()
    }
  }
  
  openModal(planDetails) {
    // Update hidden form fields and display elements
    this.updateFormFields(planDetails)
    
    // Show the modal
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      this.modalTarget.classList.add('flex')
      document.body.classList.add('overflow-hidden')
      
      // Initialize map after modal is visible
      setTimeout(() => {
        if (typeof L !== 'undefined') {
          this.initMap()
        } else {
          console.error('Leaflet library not loaded')
          this.showMapError()
        }
      }, 100)
    }
  }
  
  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      this.modalTarget.classList.remove('flex')
      document.body.classList.remove('overflow-hidden')
    }
  }
  
  updateFormFields(details) {
    const { planId, leads, planDescription, planDisplayName, pricePerBundle } = details
    
    // Update hidden form fields
    if (this.hasPlanIdTarget) this.planIdTarget.value = planId
    if (this.hasLeadsTarget) this.leadsTarget.value = leads
    
    // Update display elements
    if (this.hasPlanTitleTarget) this.planTitleTarget.textContent = `${planDisplayName} Plan`
    if (this.hasPlanDescriptionTarget) this.planDescriptionTarget.textContent = planDescription
    
    // Calculate bundles and price based on the bundle-based pricing model
    const bundleSize = 10 // Each bundle contains 10 leads
    const bundleQuantity = Math.ceil(leads / bundleSize)
    
    // Calculate total price based on bundle quantity
    const totalPrice = bundleQuantity * pricePerBundle
    
    // Update price display
    if (this.hasPlanPriceTarget) this.planPriceTarget.textContent = totalPrice
    
    // Update all lead count displays
    this.leadsTargets.forEach(target => {
      target.textContent = leads
    })
    
    // Validate form after updating
    this.validateForm()
  }
  

  
  validateForm() {
    const emailInput = document.getElementById('email');
    const zipcodeInput = document.getElementById('zipcode');
    const termsCheckbox = document.getElementById('terms');
    const submitButton = document.querySelector('button[type="submit"]');
    
    if (!submitButton) return;
    
    const emailValid = emailInput && emailInput.value && emailInput.validity.valid;
    const zipcodeValid = zipcodeInput && zipcodeInput.value && zipcodeInput.validity.valid;
    const termsAccepted = termsCheckbox && termsCheckbox.checked;
    
    if (emailValid && zipcodeValid && termsAccepted) {
      submitButton.disabled = false;
      submitButton.classList.remove('opacity-50', 'cursor-not-allowed');
      submitButton.classList.add('cursor-pointer');
    } else {
      submitButton.disabled = true;
      submitButton.classList.add('opacity-50', 'cursor-not-allowed');
      submitButton.classList.remove('cursor-pointer');
    }
  }
  
  showMapError() {
    const mapElement = document.getElementById('map');
    if (mapElement) {
      mapElement.innerHTML = '<div class="flex items-center justify-center h-full bg-gray-100 rounded-lg"><p class="text-gray-500">Unable to load map</p></div>';
    }
  }
  
  initMap() {
    try {
      const mapElement = document.getElementById('map');
      if (!mapElement) return;
      
      // If map is already initialized, just resize it and return
      if (this.map) {
        setTimeout(() => {
          this.map.invalidateSize();
        }, 300);
        return;
      }
      
      // Clear any previous map content
      mapElement.innerHTML = '';
      
      // Default location (center of US)
      const defaultLocation = [39.8283, -98.5795]; // [lat, lng] for Leaflet
      
      // Create the map
      this.map = L.map(mapElement).setView(defaultLocation, 4);
      
      // Add OpenStreetMap tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);
      
      // Add a message to enter zipcode
      const overlay = document.createElement('div');
      overlay.className = 'absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-70 z-10';
      overlay.id = 'map-overlay';
      overlay.innerHTML = '<div class="text-center p-4"><p class="text-gray-700 font-medium">Enter a US zipcode to see the location</p></div>';
      mapElement.style.position = 'relative';
      mapElement.appendChild(overlay);
      
      // Force a map resize after the modal is fully visible
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 300);
    } catch (error) {
      console.error('Error initializing map:', error);
      this.showMapError();
    }
  }
  
  geocodeZipcode(zipcode) {
    try {
      // Validate US zipcode format
      const zipcodePattern = /^\d{5}(-\d{4})?$/;
      if (!zipcodePattern.test(zipcode)) {
        alert('Please enter a valid US zipcode (e.g. 12345 or 12345-6789)');
        return;
      }
      
      if (!this.map) {
        console.error('Map not initialized');
        return;
      }
      
      // Use Nominatim for geocoding (free OpenStreetMap geocoding service)
      fetch(`https://nominatim.openstreetmap.org/search?format=json&postalcode=${zipcode}&country=USA&limit=1`)
        .then(response => response.json())
        .then(data => {
          if (data && data.length > 0) {
            const location = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
            
            // Remove overlay if it exists
            const overlay = document.getElementById('map-overlay');
            if (overlay) overlay.remove();
            
            // Update map view
            this.map.setView(location, 13);
            
            // Clear any existing markers
            this.map.eachLayer(layer => {
              if (layer instanceof L.Marker) {
                this.map.removeLayer(layer);
              }
            });
            
            // Create a marker for the location with custom icon
            const locationIcon = L.divIcon({
              className: 'location-marker',
              html: '<div style="background-color: #4F46E5; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white;"></div>',
              iconSize: [20, 20],
              iconAnchor: [10, 10]
            });
            
            // Add marker for the zipcode location
            const marker = L.marker(location, { icon: locationIcon }).addTo(this.map);
            marker.bindPopup(`<div class="text-center"><strong>${zipcode}</strong><br>We'll find properties in this area</div>`).openPopup();
          } else {
            alert('Could not find location for this zipcode. Please verify it is a valid US zipcode.');
          }
        })
        .catch(error => {
          console.error('Error geocoding zipcode:', error);
          alert('Error finding location. Please try again with a valid US zipcode.');
        });
    } catch (error) {
      console.error('Error in geocodeZipcode:', error);
    }
  }
}
